import * as React from 'react';

import { useTracker } from '@coursera/event-pulse/react';

import { CoachMode } from 'bundles/ai-coach-platform/constants';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';

export const useSearchTracking = (): {
  trackSearch: (searchTerm: string) => void;
  trackClearSearch: () => void;
  trackClickNextMatch: (searchTerm: string) => void;
  trackClickPreviousMatch: (searchTerm: string) => void;
  trackClickSkipLink: (searchTerm: string) => void;
} => {
  const track = useTracker();
  const trackingContext = useCoachTrackingContext()?.data;
  const courseData = React.useMemo(
    () => ({
      ...(trackingContext?.courseId && {
        product: {
          id: trackingContext?.courseId,
        },
      }),
      ...(trackingContext?.itemId && {
        courseItem: {
          id: trackingContext?.itemId,
        },
      }),
    }),
    [trackingContext?.courseId, trackingContext?.itemId]
  );

  React.useEffect(() => {
    track('interact_coach', {
      coachInteractionType: 'open_chat_search',
      coach: {
        mode: trackingContext?.mode ?? CoachMode.Basic,
      },
      ...courseData,
    });

    return () => {
      track('interact_coach', {
        coachInteractionType: 'close_chat_search',
        coach: {
          mode: trackingContext?.mode ?? CoachMode.Basic,
        },
        ...courseData,
      });
    };
  }, [courseData, track, trackingContext?.courseId, trackingContext?.itemId, trackingContext?.mode]);

  return {
    trackSearch: (searchTerm: string) => {
      track('interact_coach_chat_search', {
        coachChatSearchInteractionType: 'search_by_keyword',
        coachChatSearchQuery: searchTerm,
        coach: {
          mode: trackingContext?.mode ?? CoachMode.Basic,
        },
      });
    },
    trackClearSearch: () => {
      track('interact_coach_chat_search', {
        coachChatSearchInteractionType: 'clear_search',
        coach: {
          mode: trackingContext?.mode ?? CoachMode.Basic,
        },
      });
    },
    trackClickNextMatch: (searchTerm: string) => {
      track('interact_coach_chat_search', {
        coachChatSearchInteractionType: 'click_next_match',
        coachChatSearchQuery: searchTerm,
        coach: {
          mode: trackingContext?.mode ?? CoachMode.Basic,
        },
      });
    },
    trackClickPreviousMatch: (searchTerm: string) => {
      track('interact_coach_chat_search', {
        coachChatSearchInteractionType: 'click_previous_match',
        coachChatSearchQuery: searchTerm,
        coach: {
          mode: trackingContext?.mode ?? CoachMode.Basic,
        },
      });
    },
    trackClickSkipLink: (searchTerm: string) => {
      track('interact_coach_chat_search', {
        coachChatSearchInteractionType: 'click_skip_link',
        coachChatSearchQuery: searchTerm,
        coach: {
          mode: trackingContext?.mode ?? CoachMode.Basic,
        },
      });
    },
  };
};
